import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Unplug, Calendar as CalendarIcon, Zap } from "lucide-react";
import { GmailIcon, SlackIcon, NotionIcon, WhatsAppIcon, LinkedInIcon } from "@/components/BrandIcons";
import Nango from "@nangohq/frontend";

export default function Settings() {
  return (
    <div className="p-4 md:p-8 pb-24 md:pb-8 max-w-4xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-neutral-500 mt-1">Connections and integrations</p>
      </div>

      {/* Connections (Nango-managed OAuth) */}
      <NangoIntegrations />

      <p className="text-xs text-center text-neutral-500">Founder&apos;s Office OS · v1.0 · Powered by Claude</p>
    </div>
  );
}

// -----------------------------------------------------------------------------
// NangoIntegrations — Managed OAuth via Nango.
// Each provider gets one row with a Connect/Disconnect button. Clicking
// Connect mints a session token from our backend then opens Nango's hosted
// Connect UI in a modal. On success the webhook fires and our backend flips
// the row to Connected.
// -----------------------------------------------------------------------------
function NangoIntegrations() {
  const [providers, setProviders] = useState([]);
  const [busy, setBusy] = useState(null);
  const nango = useMemo(() => new Nango(), []);

  const load = async () => {
    try {
      const r = await fetch("/api/integrations/nango/connections", { credentials: "include" });
      if (!r.ok) return;
      setProviders(await r.json());
    } catch { /* ignore */ }
  };

  // On mount: reconcile with Nango (in case connections were made in another
  // session or the webhook never reached us), then load the fresh list.
  useEffect(() => {
    (async () => {
      try { await fetch("/api/integrations/nango/reconcile", { method: "POST", credentials: "include" }); } catch { /* ignore */ }
      await load();
    })();
  }, []);

  const connect = async (provider) => {
    setBusy(provider);
    try {
      const r = await fetch("/api/integrations/nango/session", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider }),
      });
      if (!r.ok) throw new Error("Could not start OAuth");
      const { sessionToken } = await r.json();
      nango.openConnectUI({
        sessionToken,
        onEvent: (evt) => {
          if (evt.type === "connect") {
            // Webhook may not reach preview envs — reconcile with Nango directly
            // so the connection status flips even without the webhook.
            (async () => {
              try { await fetch("/api/integrations/nango/reconcile", { method: "POST", credentials: "include" }); } catch { /* ignore */ }
              toast.success(`${provider} connected`);
              load();
            })();
          }
          if (evt.type === "close") { load(); }
        },
      });
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async (provider) => {
    if (!window.confirm(`Disconnect ${provider}? You'll need to re-authorize to use it again.`)) return;
    try {
      const r = await fetch(`/api/integrations/nango/connections/${provider}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error();
      toast.success("Disconnected");
      load();
    } catch {
      toast.error("Failed to disconnect");
    }
  };

  const iconFor = (p) => {
    switch (p) {
      case "gmail":    return <GmailIcon />;
      case "calendar": return <CalendarIcon size={18} className="text-neutral-700" />;
      case "slack":    return <SlackIcon />;
      case "notion":   return <NotionIcon />;
      case "linkedin": return <LinkedInIcon />;
      case "whatsapp": return <WhatsAppIcon />;
      default:         return null;
    }
  };

  return (
    <section data-testid="nango-integrations-section">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-semibold text-neutral-900">Connections</h2>
        <span className="text-[11px] text-neutral-500 flex items-center gap-1">
          <Zap size={11} className="text-neutral-400" /> Managed OAuth via Nango
        </span>
      </div>
      <div className="space-y-2">
        {providers.map((p) => (
          <div
            key={p.provider}
            className="fos-card p-4 flex items-center gap-3"
            data-testid={`nango-card-${p.provider}`}
          >
            <span className="shrink-0 flex items-center justify-center w-9 h-9 rounded-md bg-neutral-50 border border-neutral-200">
              {iconFor(p.provider)}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-neutral-900">{p.label}</span>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-sm border tracking-wide ${
                    p.connected ? "text-neutral-700 border-neutral-300" : "text-neutral-500 border-neutral-200"
                  }`}
                >
                  {p.connected ? "Connected" : "Not connected"}
                </span>
              </div>
              <p className="text-xs text-neutral-500 truncate mt-0.5">
                {p.connected
                  ? "OAuth managed by Nango · access tokens auto-refresh"
                  : "Click Connect to authorize via Nango's hosted flow"}
              </p>
            </div>
            {p.connected ? (
              <button
                onClick={() => disconnect(p.provider)}
                data-testid={`nango-disconnect-${p.provider}`}
                className="text-xs px-3 py-1.5 rounded-sm border border-neutral-200 text-neutral-700 hover:bg-neutral-100 transition-colors flex items-center gap-1"
              >
                <Unplug size={12} /> Disconnect
              </button>
            ) : (
              <button
                onClick={() => connect(p.provider)}
                disabled={busy === p.provider}
                data-testid={`nango-connect-${p.provider}`}
                className="text-xs px-3 py-1.5 rounded-sm font-medium bg-neutral-900 text-white hover:bg-neutral-800 transition-colors disabled:opacity-50"
              >
                {busy === p.provider ? "Opening…" : "Connect"}
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
